import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { AsOfDate } from '../../../../models/api/view/claim/hr/employee-detail/asofdate';
import { EmployeeWorkSchedule } from '../../../../models/api/view/claim/hr/employee-detail/employee-work-schedule';
import { PersonalEmployeeDetail } from '../../../../models/api/view/claim/hr/employee-detail/personal-employee-detail';
import { JoinPipe } from '../../../../pipes/join.pipe';
import { BaseComponent } from '../../../base/base.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { AddressLabelComponent } from '../../../core/labels/address-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { SsnLabelComponent } from '../../../core/labels/ssn-label.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
@Component({
  selector: 'app-employee-detail',
  imports: [
    CommonModule,
    RouterModule,
    DetailPanelComponent,
    DetailItemComponent,
    PageHeaderComponent,
    AddressLabelComponent,
    CardComponent,
    TableComponent,
    ColumnComponent,
    ActionRowComponent,
    SelectComponent,
    SsnLabelComponent,
    JoinPipe,
  ],
  template: `
    <app-page-header label="Employee Detail" />
    <div class="tw-flex tw-gap-8 tw-px-4">
      <div class="tw-basis-1/2">
        <app-card label="general">
          <div class="tw-flex tw-w-full tw-gap-8">
            <app-detail-panel class="tw-grow" [dataSource]="edpersonal">
              <app-detail-item label="name" name="lastName" [showEmpty]="false">
                <ng-template #value let-data>
                  {{
                    [data.firstName, data.middleInitial, data.lastName] | join
                  }}
                </ng-template>
              </app-detail-item>
              <!-- #341516: Emp ID ssn-label only when equals SSN and not already masked (same pattern as Summary Leave card) -->
              <app-detail-item
                label="Employee ID"
                name="employeeID"
                format="s"
                labelCase="none"
                [showEmpty]="false"
              >
                <ng-template #value>
                  @if (
                    edpersonal?.ssn &&
                    edpersonal?.employeeID === edpersonal?.ssn &&
                    !edpersonal.employeeID.includes('***')
                  ) {
                    <app-ssn-label
                      [value]="edpersonal.employeeID"
                      [canUnmaskSsn]="edpersonal.isUnmaskSSN"
                    />
                  } @else {
                    <span>{{ edpersonal?.employeeID }}</span>
                  }
                </ng-template>
              </app-detail-item>

              <app-detail-item
                label="Date of Birth"
                name="dateOfBirth"
                format="d"
                [showEmpty]="false"
              />
              @if (suppressWorkSchedule) {
                <app-detail-item
                  label="work State"
                  name="workState"
                  [showEmpty]="false"
                />
              }
              <app-detail-item
                label="Date of Death"
                name="dateOfDeath"
                format="d"
                [showEmpty]="true"
              />
              <app-detail-item
                label="pay Cycle"
                name="payCycle"
                [showEmpty]="false"
              />
              <app-detail-item label="sex" name="sex" [showEmpty]="false" />
              <app-detail-item
                label="job Title"
                name="jobTitle"
                [showEmpty]="false"
              />
              <app-detail-item
                label="full/Part Time"
                name="fullPartTime"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Direct Supervisor"
                labelCase="none"
                name="directSupervisor"
                [showEmpty]="false"
              >
                <ng-template #value let-data>
                  {{ data.directSupervisor | titlecase }}
                </ng-template>
              </app-detail-item>
            </app-detail-panel>
            <app-detail-panel class="tw-grow" [dataSource]="edpersonal">
              <app-detail-item
                label="SSN"
                name="ssn"
                format="s"
                labelCase="none"
                [showEmpty]="false"
              >
                <ng-template #value>
                  <app-ssn-label
                    [value]="edpersonal.ssn"
                    [canUnmaskSsn]="edpersonal.isUnmaskSSN"
                  />
                </ng-template>
              </app-detail-item>

              <app-detail-item
                label="hire Date"
                name="dateOfHire"
                format="d"
                [showEmpty]="false"
              />
              <app-detail-item
                label="termination Date"
                name="dateTerminated"
                format="d"
                [showEmpty]="true"
              />
              <app-detail-item
                label="rehire Date"
                name="dateOfRehire"
                format="d"
                [showEmpty]="false"
              />
              <app-detail-item
                label="NCS Date"
                labelCase="none"
                name="ncsDate"
                format="d"
                [showEmpty]="false"
              />
              <app-detail-item
                label="marital Status"
                name="maritalStatus"
                [showEmpty]="false"
              />
            </app-detail-panel>
          </div>
        </app-card>
      </div>
      <div class="tw-basis-1/4">
        <app-card label="home">
          <app-detail-panel [dataSource]="edpersonal">
            <app-detail-item
              label="address"
              name="homeAddress"
              [showEmpty]="false"
              [wrapValue]="true"
            >
              <ng-template let-data #value>
                <app-address-label
                  [street1]="data.homeAddress"
                  [city]="data.homeCity"
                  [state]="data.homeState"
                  [postalCode]="data.homePostalCode"
                  [country]="data.homeCountry"
                  [wrapText]="true"
                />
              </ng-template>
            </app-detail-item>

            <app-detail-item
              label="home Phone"
              name="homePhone"
              format="p"
              [showEmpty]="false"
            />
            <app-detail-item
              label="email"
              name="homeEmail"
              [showEmpty]="true"
            />
          </app-detail-panel>
        </app-card>
      </div>
      @if (!suppressWorkSchedule) {
        <div class="tw-basis-1/4">
          <app-card label="work">
            <app-detail-panel [dataSource]="edpersonal">
              <app-detail-item
                label="address"
                name="workAddress"
                [showEmpty]="false"
                [wrapValue]="true"
              >
                <ng-template let-data #value>
                  <app-address-label
                    [street1]="data.workAddress"
                    [city]="data.workCity"
                    [state]="data.workState"
                    [postalCode]="data.workPostalCode"
                    [country]="data.workCountry"
                    [wrapText]="true"
                  />
                </ng-template>
              </app-detail-item>

              <app-detail-item
                label="work Phone"
                name="workPhone"
                format="p"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Fax"
                name="workFax"
                format="p"
                [showEmpty]="true"
              />
              <app-detail-item
                label="email"
                name="workEmail"
                [showEmpty]="false"
              />
              <app-detail-item
                label="pay Group"
                name="payGroup"
                [showEmpty]="false"
              />
              <app-detail-item
                label="business Unit"
                name="businessUnit"
                [showEmpty]="false"
              />
              <app-detail-item
                label="responsibility Code"
                name="responsibilityCode"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Department ID"
                labelCase="none"
                name="departmentID"
                [showEmpty]="false"
              />
              <app-detail-item
                label="division Code"
                name="divisionCode"
                [showEmpty]="false"
              />
              <app-detail-item
                label="bargained Unit"
                name="bargainedUnit"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location Name"
                name="locationName"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location City"
                name="locationCity"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location State"
                name="locationState"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Maildrop ID"
                labelCase="none"
                name="mailDropID"
                [showEmpty]="true"
              />
            </app-detail-panel>
          </app-card>
        </div>
      }
    </div>
    @if (suppressWorkSchedule) {
      <div class="tw-flex tw-w-full tw-gap-8 tw-px-4 tw-pt-6">
        <div class="tw-basis-1/2">
          <app-card label="work">
            <app-detail-panel [dataSource]="edpersonal">
              <app-detail-item
                label="address"
                name="workAddress"
                [showEmpty]="false"
                [wrapValue]="true"
              >
                <ng-template let-data #value>
                  <app-address-label
                    [street1]="data.workAddress"
                    [city]="data.workCity"
                    [state]="data.workState"
                    [postalCode]="data.workPostalCode"
                    [country]="data.workCountry"
                    [wrapText]="true"
                  />
                </ng-template>
              </app-detail-item>

              <app-detail-item
                label="work Phone"
                name="workPhone"
                format="p"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Fax"
                name="workFax"
                format="p"
                [showEmpty]="true"
              />
              <app-detail-item
                label="email"
                name="workEmail"
                [showEmpty]="false"
              />
              <app-detail-item
                label="pay Group"
                name="payGroup"
                [showEmpty]="false"
              />
              <app-detail-item
                label="business Unit"
                name="businessUnit"
                [showEmpty]="false"
              />
              <app-detail-item
                label="responsibility Code"
                name="responsibilityCode"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Department ID"
                labelCase="none"
                name="departmentID"
                [showEmpty]="false"
              />
              <app-detail-item
                label="division Code"
                name="divisionCode"
                [showEmpty]="false"
              />
              <app-detail-item
                label="bargained Unit"
                name="bargainedUnit"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location Name"
                name="locationName"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location City"
                name="locationCity"
                [showEmpty]="false"
              />
              <app-detail-item
                label="location State"
                name="locationState"
                [showEmpty]="false"
              />
              <app-detail-item
                label="Maildrop ID"
                labelCase="none"
                name="mailDropID"
                [showEmpty]="true"
              />
            </app-detail-panel>
          </app-card>
        </div>
      </div>
    }
    @if (!suppressWorkSchedule) {
      <div class="tw-flex tw-w-full tw-gap-8 tw-px-4 tw-pt-6">
        <app-card
          label="work schedule"
          class="tw-w-full"
          [showExpand]="true"
          [expanded]="false"
        >
          <app-action-row>
            <div class="tw-w-64" right1>
              <app-select
                [control]="asOfDate"
                [options]="asOfDateList"
                optionLabel="key"
                optionValue="value"
                [multiple]="false"
                [showClear]="false"
                [showHelpText]="false"
                (change)="onSelectChange($event)"
                label="As of"
              ></app-select>
            </div>
          </app-action-row>
          <app-table
            [dataSource]="wschedule"
            [pageSize]="10"
            [scrollable]="false"
            [showFilter]="false"
            recordLabel="week"
          >
            <app-column name="dateRange" label="Week" />
            <app-column name="day1" label="Sunday" />
            <app-column name="day2" label="Monday" />
            <app-column name="day3" label="Tuesday" />
            <app-column name="day4" label="Wednesday" />
            <app-column name="day5" label="Thursday" />
            <app-column name="day6" label="Friday" />
            <app-column name="day7" label="Saturday" />
          </app-table>
        </app-card>
      </div>
    }
  `,
  styles: ``,
})
export class EmployeeDetailComponent extends BaseComponent implements OnInit {
  suppressWorkSchedule = false;
  claim: ClaimRow;
  edpersonal: PersonalEmployeeDetail;
  wschedule: EmployeeWorkSchedule[] = [];
  asOfDateList: any;
  asOfdate: AsOfDate;
  asOfDate = new FormControl<string>('e');
  async ngOnInit() {
    const claim = this.view.currentClaim();
    this.claim = claim;

    this.suppressWorkSchedule = this.claim.recordSource === 'R';

    if (!this.suppressWorkSchedule) {
      this.dispose.subs = this.asOfDate.valueChanges.subscribe(
        (selectedDate: string) => {
          this.onSelectChange(selectedDate);
        },
      );
    }
    this.edpersonal = await this.api.employeeDetail.getPersonal(
      this.claim.recordSource,
      this.claim.recordId,
      this.claim.contractNumber,
    );
    if (!this.suppressWorkSchedule) {
      this.asOfDateList = (
        await this.api.employeeDetail.getWorkScheduleAsOfNowDate(
          this.claim.recordSource,
          this.claim.recordId,
        )
      ).asOfDate;

      if (this.asOfDateList.length > 0) {
        this.asOfDate.setValue(this.asOfDateList[0].value);
      }
    }
    this.view.hideLoading();
  }

  async onSelectChange(value: any) {
    this.asOfdate = { asOfDate: new Date(Date.parse(value)) };
    this.wschedule = (
      await this.api.employeeDetail.getWorkScheduleAsOfDate(
        this.claim.recordSource,
        this.claim.recordId,
        this.claim.contractNumber,
        this.asOfdate,
      )
    ).workSchedule;
  }
}
